import React, { useState, useEffect } from 'react';
import SearchableSelect from '../ui/SearchableSelect';
import { Accommodation } from '../../types';
import api from '../../services/api';
import RoomConfigBuilder from './RoomConfigBuilder';

interface Props {
  accommodations: Accommodation[];
  travelDate: string;
  noOfDays: number;
  noOfChildren: number;
  noOfPax: number;
  childrenAges: number[];
  families?: any[];
  onChange: (accommodations: Accommodation[]) => void;
  forceSimpleMode?: boolean;
  forceAdvancedMode?: boolean;
  onValidationChange?: (isValid: boolean) => void;
  onHotelDetailsChange?: (details: Record<number, any>) => void;
}

const AccommodationsForm: React.FC<Props> = ({ accommodations, travelDate, noOfDays, noOfChildren, noOfPax, childrenAges, families, onChange, forceSimpleMode, forceAdvancedMode, onValidationChange, onHotelDetailsChange }) => {
  const categories = ['3 Star', '4 Star', '5 Star', '4* Pool Villa', '5* Pool Villa'];
  const [advancedMode, setAdvancedMode] = useState<Record<number, boolean>>({});
  const [availableRooms, setAvailableRooms] = useState<Record<number, Record<string, number>>>({});
  const [roomValidationStates, setRoomValidationStates] = useState<Record<number, boolean>>({});
  const [hotelDetailsMap, setHotelDetailsMap] = useState<Record<number, any>>({});

  // Track validation state from all room configurations
  React.useEffect(() => {
    const allValid = Object.values(roomValidationStates).every(isValid => isValid !== false);
    onValidationChange?.(allValid);
  }, [roomValidationStates, onValidationChange]);

  const handleRoomValidationChange = (index: number, isValid: boolean) => {
    setRoomValidationStates(prev => ({ ...prev, [index]: isValid }));
  };

  const handleHotelDetailsLoaded = (index: number, details: any) => {
    console.log(`Hotel details loaded for accommodation ${index}:`, details);
    setHotelDetailsMap(prev => {
      const updated = { ...prev, [index]: details };
      console.log('Updated hotelDetailsMap:', updated);
      onHotelDetailsChange?.(updated);
      return updated;
    });
  };

  const addHotel = () => {
    const departureDate = new Date(travelDate);
    departureDate.setDate(departureDate.getDate() + noOfDays - 1);
    onChange([...accommodations, {
      hotel_id: -1,
      hotel_name: '',
      room_id: -1,
      room_type: '',
      category: '3 Star',
      checkin_date: travelDate,
      checkout_date: departureDate.toISOString().split('T')[0],
      no_of_rooms: 1,
      no_of_nights: noOfDays - 1,
      no_of_extra_beds: 0,
      no_kids_for_bf: noOfChildren,
      foc: 0
    }]);
  };

  const removeHotel = () => {
    if (accommodations.length > 0) {
      onChange(accommodations.slice(0, -1));
    }
  };

  const updateAccommodation = (index: number, updates: Partial<Accommodation>) => {
    const updated = [...accommodations];
    updated[index] = { ...updated[index], ...updates };
    
    // Auto-calculate nights when dates change
    if (updates.checkin_date || updates.checkout_date) {
      const checkin = new Date(updated[index].checkin_date);
      const checkout = new Date(updated[index].checkout_date);
      const nights = Math.ceil((checkout.getTime() - checkin.getTime()) / (1000 * 60 * 60 * 24));
      updated[index].no_of_nights = nights > 0 ? nights : 1;
    }
    
    onChange(updated);
  };

  const toggleAdvancedMode = (index: number) => {
    setAdvancedMode(prev => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <div className="space-y-6">
      {/* Pax Information Display */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-blue-700 font-medium">👥 Adults: {noOfPax}</span>
          <span className="text-blue-700 font-medium">👶 Children: {noOfChildren}</span>
          {childrenAges?.length > 0 && (
            <span className="text-blue-700">Ages: {childrenAges.join(', ')}y</span>
          )}
        </div>
      </div>
      <div className="flex gap-4 mb-6">
        <button onClick={addHotel} className="btn-primary">
          ➕ Add Hotel
        </button>
        <button onClick={removeHotel} className="btn-secondary" disabled={accommodations.length === 0}>
          ➖ Remove Hotel
        </button>
      </div>

      {accommodations.map((acc, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Hotel {index + 1}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Check-in Date</label>
              <input
                type="date"
                value={acc.checkin_date}
                onChange={(e) => updateAccommodation(index, { checkin_date: e.target.value })}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Check-out Date</label>
              <input
                type="date"
                value={acc.checkout_date}
                onChange={(e) => updateAccommodation(index, { checkout_date: e.target.value })}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={acc.category}
                onChange={(e) => updateAccommodation(index, { category: e.target.value })}
                className="input-field"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nights</label>
              <input
                type="number"
                value={acc.no_of_nights}
                disabled
                className="input-field bg-gray-100"
              />
            </div>
          </div>

          <HotelRoomSelector
            accommodation={acc}
            index={index}
            onUpdate={updateAccommodation}
            onRoomsLoaded={(rooms) => setAvailableRooms(prev => ({ ...prev, [index]: rooms }))}
            onHotelDetailsLoaded={(details) => handleHotelDetailsLoaded(index, details)}
            hideRoomType={(advancedMode[index] || forceAdvancedMode) && !forceSimpleMode}
          />

          {/* Advanced Mode Toggle - only show if not forced */}
          {!forceSimpleMode && !forceAdvancedMode && (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => toggleAdvancedMode(index)}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                {advancedMode[index] ? '◀ Simple Mode' : '▶ Advanced: Family/Room Split'}
              </button>
            </div>
          )}

          {(advancedMode[index] || forceAdvancedMode) && !forceSimpleMode ? (
            <RoomConfigBuilder
              configs={(acc as any).room_details?.configs || [{ quantity: acc.no_of_rooms, extra_beds: acc.no_of_extra_beds, kids_bf: acc.no_kids_for_bf }]}
              onChange={(configs) => {
                // Set the first config's room as the default top-level room
                const firstConfig = configs[0];
                const updates: any = {
                  room_details: { configs },
                  no_of_rooms: configs.reduce((sum, c) => sum + c.quantity, 0),
                  no_of_extra_beds: configs.reduce((sum, c) => sum + c.extra_beds, 0),
                  no_kids_for_bf: configs.reduce((sum, c) => sum + c.kids_bf, 0),
                  foc: configs.reduce((sum, c) => sum + (c.foc || 0), 0)
                };
                // Only update room_id/room_type if first config has them
                if (firstConfig?.room_id && firstConfig?.room_type) {
                  updates.room_id = firstConfig.room_id;
                  updates.room_type = firstConfig.room_type;
                }
                updateAccommodation(index, updates);
              }}
              availableRooms={availableRooms[index] || {}}
              hotelName={acc.hotel_name}
              families={families}
              onValidationChange={(isValid) => handleRoomValidationChange(index, isValid)}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rooms</label>
                <input
                  type="number"
                  min="1"
                  value={acc.no_of_rooms}
                  onChange={(e) => updateAccommodation(index, { no_of_rooms: parseInt(e.target.value) || 1 })}
                  className="input-field"
                />
              </div>
              <div>
                <div className="relative group">
                  <label className="block text-sm font-medium text-gray-700 mb-2 cursor-help">FOC</label>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                    FOC: Free of Cost rooms (not billed)
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
                <input
                  type="number"
                  min="0"
                  value={acc.foc || 0}
                  onChange={(e) => updateAccommodation(index, { foc: parseInt(e.target.value) || 0 })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Extra Beds</label>
                <input
                  type="number"
                  min="0"
                  value={acc.no_of_extra_beds}
                  onChange={(e) => updateAccommodation(index, { no_of_extra_beds: parseInt(e.target.value) || 0 })}
                  className={`input-field ${
                    acc.no_of_extra_beds > 0 && hotelDetailsMap[index] && hotelDetailsMap[index].extra_bed_cost === 0
                      ? 'border-red-500 bg-red-50'
                      : ''
                  }`}
                />
                {acc.no_of_extra_beds > 0 && hotelDetailsMap[index] && hotelDetailsMap[index].extra_bed_cost === 0 && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <span>⚠️</span>
                    <span>Extra bed cost is 0 for this hotel</span>
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kids for Breakfast</label>
                <input
                  type="number"
                  min="0"
                  value={acc.no_kids_for_bf}
                  onChange={(e) => updateAccommodation(index, { no_kids_for_bf: parseInt(e.target.value) || 0 })}
                  className="input-field"
                />
              </div>
            </div>
          )}
        </div>
      ))}

      {accommodations.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No hotels added yet. Click "Add Hotel" to get started.
        </div>
      )}
    </div>
  );
};

const HotelRoomSelector: React.FC<{
  accommodation: Accommodation;
  index: number;
  onUpdate: (index: number, updates: Partial<Accommodation>) => void;
  onRoomsLoaded: (rooms: Record<string, number>) => void;
  onHotelDetailsLoaded?: (details: any) => void;
  hideRoomType?: boolean;
}> = ({ accommodation, index, onUpdate, onRoomsLoaded, onHotelDetailsLoaded, hideRoomType }) => {
  const [hotels, setHotels] = useState<Record<string, number>>({});
  const [rooms, setRooms] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [hotelDetails, setHotelDetails] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(true);

  useEffect(() => {
    if (accommodation.checkin_date && accommodation.checkout_date && accommodation.category) {
      loadHotels();
    }
  }, [accommodation.checkin_date, accommodation.checkout_date, accommodation.category]);

  useEffect(() => {
    if (accommodation.hotel_id && accommodation.hotel_id !== -1) {
      loadRooms();
    }
  }, [accommodation.hotel_id, accommodation.checkin_date, accommodation.checkout_date]);

  const loadHotels = async () => {
    try {
      setLoading(true);
      const response = await api.get('/itineraries/hotels/search', {
        params: {
          checkin_date: accommodation.checkin_date,
          checkout_date: accommodation.checkout_date,
          category: accommodation.category
        }
      });
      setHotels(response.data || {});
    } catch (error) {
      console.error('Error loading hotels:', error);
      setHotels({});
    } finally {
      setLoading(false);
    }
  };

  const loadRooms = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/itineraries/hotels/${accommodation.hotel_id}/rooms`, {
        params: {
          checkin_date: accommodation.checkin_date,
          checkout_date: accommodation.checkout_date
        }
      });
      console.log('Hotel rooms response:', response.data);
      setRooms(response.data.rooms || {});
      onRoomsLoaded(response.data.rooms || {});
      setHotelDetails(response.data.hotel_details);
      onHotelDetailsLoaded?.(response.data.hotel_details);
      setShowDetails(true);
      console.log('Hotel details set to:', response.data.hotel_details);
      if (response.data.message && response.data.message.includes('Error')) {
        console.warn('Room loading issue:', response.data.message);
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
      setRooms({});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className={`grid grid-cols-1 ${hideRoomType ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-4`}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hotel Name {loading && <span className="text-xs text-gray-500">(Loading...)</span>}
          </label>
          <SearchableSelect
            options={Object.entries(hotels).map(([name, id]) => ({ value: id, label: name }))}
            value={accommodation.hotel_id !== -1 ? accommodation.hotel_id : ''}
            onChange={(value) => {
              const selectedOption = Object.entries(hotels).find(([name, id]) => id === value);
              onUpdate(index, {
                hotel_id: value ? value as number : -1,
                hotel_name: selectedOption ? selectedOption[0] : '',
                room_id: -1,
                room_type: ''
              });
            }}
            placeholder="SELECT HOTEL"
            disabled={loading || Object.keys(hotels).length === 0}
            className="text-sm"
          />
          {Object.keys(hotels).length === 0 && !loading && (
            <p className="text-xs text-orange-600 mt-1">⚠️ No hotels available. Try different dates or restart backend.</p>
          )}
        </div>
        {!hideRoomType && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Room Type</label>
          <SearchableSelect
            options={Object.entries(rooms).map(([type, id]) => ({ value: id, label: type }))}
            value={accommodation.room_id !== -1 ? accommodation.room_id : ''}
            onChange={(value) => {
              const selectedOption = Object.entries(rooms).find(([type, id]) => id === value);
              onUpdate(index, {
                room_id: value ? value as number : -1,
                room_type: selectedOption ? selectedOption[0] : ''
              });
            }}
            placeholder="SELECT ROOM"
            disabled={loading || accommodation.hotel_id === -1 || Object.keys(rooms).length === 0}
            className="text-sm"
          />
          {Object.keys(rooms).length === 0 && !loading && accommodation.hotel_id !== -1 && (
            <p className="text-xs text-orange-600 mt-1">⚠️ No rooms available. Backend may have an error.</p>
          )}
        </div>
        )}
      </div>

      {hotelDetails && (
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-300 rounded-lg shadow-sm transition-all p-3">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center gap-1.5">
              <span className="text-sm">🏨</span>
              <h4 className="font-semibold text-xs text-blue-900">{hotelDetails.hotel_name} {'⭐'.repeat(parseInt(hotelDetails.star_rating?.replace(' Star', '') || '0'))}</h4>
            </div>
            <span className="text-blue-600">{showDetails ? '▼' : '▶'}</span>
          </button>
          {showDetails && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
            <div className="bg-white rounded p-1.5 border border-blue-100">
              <span className="text-[10px] text-gray-500 block">📍 Area</span>
              <span className="font-medium text-xs text-gray-900">{hotelDetails.area}</span>
            </div>
            <div className="bg-white rounded p-1.5 border border-blue-100">
              <span className="text-[10px] text-gray-500 block">👶 Kids BF Age</span>
              <span className="font-medium text-xs text-gray-900">{hotelDetails.kids_min_age_for_breakfast}y</span>
            </div>
            <div className="bg-white rounded p-1.5 border border-blue-100">
              <span className="text-[10px] text-gray-500 block">💰 Kids BF</span>
              <span className="font-medium text-xs text-green-700">{hotelDetails.kids_breakfast_cost?.toLocaleString()}</span>
            </div>
            <div className="bg-white rounded p-1.5 border border-blue-100">
              <span className="text-[10px] text-gray-500 block">🛏️ Bed Age</span>
              <span className="font-medium text-xs text-gray-900">{hotelDetails.extra_bed_min_age}y</span>
            </div>
            <div className="bg-white rounded p-1.5 border border-blue-100">
              <span className="text-[10px] text-gray-500 block">💰 Bed Cost</span>
              <span className="font-medium text-xs text-green-700">{hotelDetails.extra_bed_cost?.toLocaleString()}</span>
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccommodationsForm;
